'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import { m, useMotionValue, useAnimation, PanInfo } from 'framer-motion'
import Image from 'next/image'
import { type MediaItem } from '@/data/projects'
import Lightbox from '@/components/ui/Lightbox'

interface CarouselProps {
  items: MediaItem[]
}

export default function Carousel({ items }: CarouselProps) {
  const [current, setCurrent] = useState(0)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([])
  const x = useMotionValue(0)
  const controls = useAnimation()

  const goTo = useCallback((index: number) => {
    const clamped = Math.max(0, Math.min(index, items.length - 1))
    const containerWidth = containerRef.current?.offsetWidth ?? 0
    setCurrent(clamped)
    controls.start({ x: -clamped * containerWidth, transition: { type: 'spring', stiffness: 300, damping: 35 } })
  }, [items.length, controls])

  const prev = () => goTo(current - 1)
  const next = () => goTo(current + 1)

  useEffect(() => {
    videoRefs.current.forEach((vid, i) => {
      if (!vid) return
      if (i === current) {
        vid.play().catch(() => {})
      } else {
        vid.pause()
        vid.currentTime = 0
      }
    })
  }, [current])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (lightboxOpen) return
      if (e.key === 'ArrowLeft') prev()
      if (e.key === 'ArrowRight') next()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [current, lightboxOpen])

  const handleDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    setIsDragging(false)
    const threshold = 50
    const velocity = info.velocity.x
    const offset = info.offset.x

    if (offset < -threshold || velocity < -500) {
      goTo(current + 1)
    } else if (offset > threshold || velocity > 500) {
      goTo(current - 1)
    } else {
      goTo(current)
    }
  }

  if (items.length === 0) return null

  return (
    <>
      <div className="relative w-full rounded-xl dark:bg-batman-black bg-gray-100 select-none p-2">
        {/* Slides track */}
        <div className="relative w-full aspect-video overflow-hidden rounded-lg" ref={containerRef}>
          <m.div
            className="flex h-full"
            style={{ x, width: `${items.length * 100}%` }}
            animate={controls}
            drag="x"
            dragConstraints={{ left: -(items.length - 1) * (containerRef.current?.offsetWidth ?? 0), right: 0 }}
            dragElastic={0.1}
            onDragStart={() => setIsDragging(true)}
            onDragEnd={handleDragEnd}
          >
            {items.map((item, i) => (
              <div
                key={i}
                className="relative h-full cursor-pointer"
                style={{ width: `${100 / items.length}%` }}
                onClick={() => { if (!isDragging) setLightboxOpen(true) }}
              >
                {item.type === 'video' ? (
                  <video
                    ref={el => { videoRefs.current[i] = el }}
                    src={item.src}
                    poster={item.poster}
                    preload="none"
                    muted
                    loop
                    playsInline
                    className="w-full h-full object-cover relative z-10"
                  />
                ) : item.type === 'youtube' ? (
                  <div className="relative w-full h-full z-10">
                    <Image
                      src={`https://img.youtube.com/vi/${item.src.split('/embed/')[1]?.split('?')[0]}/maxresdefault.jpg`}
                      alt={item.caption ?? `Slide ${i + 1}`}
                      fill
                      sizes="(min-width: 1072px) 1024px, calc(100vw - 48px)"
                      className="object-cover"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <div className="w-16 h-16 rounded-full bg-batman-gold/90 flex items-center justify-center shadow-lg">
                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-black ml-1">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Image
                    src={item.src}
                    alt={item.caption ?? `Slide ${i + 1}`}
                    fill
                    sizes="(min-width: 1072px) 1024px, calc(100vw - 48px)"
                    priority={i === 0}
                    className="object-cover relative z-10"
                    onError={(e) => { e.currentTarget.style.display = 'none' }}
                  />
                )}

                {/* Fallback placeholder when no real image */}
                <div className="absolute inset-0 flex items-center justify-center dark:bg-batman-surface bg-gray-200">
                  <svg viewBox="0 0 100 60" fill="currentColor" className="w-20 opacity-10 text-batman-gold">
                    <path d="M50 10 C40 10 30 20 20 18 C10 16 0 22 2 30 C4 36 12 32 18 35 C24 38 28 45 35 43 C38 42 42 38 50 38 C58 38 62 42 65 43 C72 45 76 38 82 35 C88 32 96 36 98 30 C100 22 90 16 80 18 C70 20 60 10 50 10 Z M35 43 C32 50 28 55 22 55 C28 52 30 48 32 45 Z M65 43 C68 50 72 55 78 55 C72 52 70 48 68 45 Z" />
                  </svg>
                </div>

                {/* Click to zoom hint */}
                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="bg-black/50 text-white text-xs px-2 py-1 rounded font-mono">⊞ zoom</div>
                </div>
              </div>
            ))}
          </m.div>

          {/* Caption */}
          {items[current]?.caption && (
            <m.div
              key={current}
              className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-5 py-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <p className="font-mono text-xs text-white/80">{items[current].caption}</p>
            </m.div>
          )}

          {/* Prev arrow */}
          {current > 0 && (
            <m.button
              onClick={prev}
              className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full dark:bg-batman-black/70 bg-white/70 backdrop-blur flex items-center justify-center dark:text-batman-text text-bruce-navy hover:text-batman-gold dark:hover:text-batman-gold transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
            >
              ←
            </m.button>
          )}

          {/* Next arrow */}
          {current < items.length - 1 && (
            <m.button
              onClick={next}
              className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full dark:bg-batman-black/70 bg-white/70 backdrop-blur flex items-center justify-center dark:text-batman-text text-bruce-navy hover:text-batman-gold dark:hover:text-batman-gold transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
            >
              →
            </m.button>
          )}
        </div>

        {/* Dots + counter */}
        {items.length > 1 && (
          <div className="flex items-center justify-center gap-3 py-3">
            <div className="flex gap-1.5">
              {items.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`rounded-full transition-all duration-300 ${i === current ? 'w-5 h-1.5 bg-batman-gold' : 'w-1.5 h-1.5 dark:bg-batman-muted/40 bg-bruce-muted/40'}`}
                />
              ))}
            </div>
            <span className="font-mono text-xs dark:text-batman-muted text-bruce-muted">
              {current + 1} / {items.length}
            </span>
          </div>
        )}
      </div>

      <Lightbox
        items={items}
        initialIndex={current}
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  )
}
